import dateformat from 'dateformat';
import { range } from 'lodash';
import { intersection, isEmpty, uniq } from 'lodash/fp';
import {
    DataCondition,
    DataFilters,
    DataGranularity,
    DataGranularityKey,
    DataType,
    TimeUnitKey,
} from 'types';

import { CONDITION_DETAIL_NOTES } from '../models/condition-detail-notes';
import {
    AGE_GROUP_NAME_KEY,
    AGE_GROUP_RANGE_KEY,
    AGE_KEY,
    CONDITION_LEVEL_KEY,
    CONSTANT_VACCINATION_ID_KEY,
    DAY_KEY,
    EXPANDED_WOLBACHIA_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    MONTH_COUNT_KEY,
    PRIMARY_ENTITY_ID_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    YEAR_KEY,
    getTimeUnitKeys,
    getValueKeys,
} from '../models/data-key';
import { AllDataTypes, SevRecords } from '../models/data-type';
import { getDataTypePrimaryEntityKey } from '../models/data-type';
import { decodeCDN } from './formatting/decodeCDN';

export const valueToArray = (
    value: string | number | undefined
): (string | number)[] | undefined => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined;
    }

    if (typeof value === 'number') {
        return [value];
    }

    return value.split(',');
};

export const valueToNumberArray = (value: string | number | undefined): number[] | undefined => {
    const multiValue = valueToArray(value);
    if (!multiValue) {
        return undefined;
    }

    return multiValue.map(Number);
};

export const numberArrayToValue = (arr: number[] | undefined) => {
    if (arr == null) {
        return undefined;
    }

    return arr.join(',');
};

export const firstAndLastFromArray = (arr: number[] | undefined): [number, number] | undefined => {
    if (arr == null) {
        return undefined;
    }
    return [arr.slice(0, 1)[0], arr.slice(-1)[0]];
};

export const minMaxTupleToRangeArray = ([min, max]: [number, number]): number[] =>
    range(min, max + 1);

export const formatTimeUnitRangeValue = (
    timeUnitKey: TimeUnitKey,
    value: number[] | undefined
): string | undefined => {
    const multiValue = value;
    if (!multiValue) {
        return undefined;
    }

    const timeUnitsTuple = firstAndLastFromArray(multiValue.sort());
    if (!timeUnitsTuple) {
        return undefined;
    }

    // range slider min/max is the same
    if (timeUnitsTuple[0] === timeUnitsTuple[1]) {
        timeUnitsTuple.pop();
    }
    return timeUnitsTuple.map((timeUnit) => formatValue(timeUnitKey, timeUnit)).join('-');
};

export const mapGranularityKeyToLocalePrefix = (
    filterKey: string,
    overrides: Record<string, string> = {},
    dataType: DataType = AllDataTypes
) => {
    const OVERRIDES_BY_DATA_TYPE = {
        [SevRecords]: {
            ['long_metric_']: 'sev_records_long_metric_',
        },
    };
    const NON_LOCALIZABLE_KEYS = [
        PRIMARY_ENTITY_ID_KEY,
        AGE_GROUP_RANGE_KEY,
        AGE_KEY,
        AGE_GROUP_NAME_KEY,
        CONDITION_LEVEL_KEY,
        ...getTimeUnitKeys(),
        ...getValueKeys(),
    ];

    let prefix = filterKey;

    if (filterKey in overrides) {
        prefix = overrides[filterKey];
    } else if (NON_LOCALIZABLE_KEYS.includes(filterKey)) {
        prefix = null;
    } else if (filterKey === PRIMARY_ENTITY_ID_LOCALIZATION_KEY) {
        prefix = '';
    } else if (filterKey.endsWith('_id')) {
        prefix = filterKey.slice(0, -2);
    }

    // TODO: why we need it, what was the requirement?
    // why not used overrides param to handle special case?
    if (prefix && OVERRIDES_BY_DATA_TYPE[dataType] && prefix in OVERRIDES_BY_DATA_TYPE[dataType]) {
        prefix = OVERRIDES_BY_DATA_TYPE[dataType][prefix];
    }

    return prefix;
};

export const getLocalePrefixForGranularityKey = (
    granularityKey: DataGranularityKey,
    overrides: Record<string, string> = {}
) => {
    return mapGranularityKeyToLocalePrefix(granularityKey, overrides);
};

export const formatValue = (
    granularityKey: string,
    value: string | number | number[] | undefined,
    _?: Function = (value) => value,
    overrides?: Record<string, string>,
    dataType: DataType = AllDataTypes
): string | number | number[] | null => {
    if (CONDITION_DETAIL_NOTES.includes(granularityKey)) {
        const decodedCDN = decodeCDN({ key: granularityKey, value });
        return decodedCDN ? decodedCDN.value : decodedCDN;
    }

    if (value == null) {
        if (granularityKey === FORECAST_SCENARIO_ID_KEY) {
            value = 0;
        } else {
            return null;
        }
    }

    if (granularityKey === DAY_KEY) {
        return dateformat(value, 'mmm d, yyyy', true);
    }

    const localePrefix = mapGranularityKeyToLocalePrefix(granularityKey, overrides, dataType);
    if (localePrefix == null) {
        return value;
    }

    const values = Array.isArray(value) ? value : valueToArray(value) || [];
    return values.map((_value) => _(localePrefix + _value)).join(', ');
};

export const noneOfSelectedOptionsIsAllowedByGranularity =
    (selectedRefinementFilters: DataFilters, refinedGranularity: DataGranularity) =>
    (filterKey) => {
        const value = selectedRefinementFilters[filterKey];
        if (value == null) {
            return false;
        }

        return value.every((i) => {
            return !(refinedGranularity[filterKey] || []).includes(i);
        });
    };

export const getConditionsDataTypes = (conditions: DataCondition[]) =>
    uniq(conditions.map((i) => i.data_type));

// it is related to utility/data-loader/getFilters, but will not add selected conditions as filters
export const filterValuesByGranularity = (filters: DataFilters, granularity: DataGranularity) => {
    if (!granularity) {
        return filters;
    }

    const refinedFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
        const values = value;
        if (values && granularity && granularity[key]) {
            const refinedValues = intersection(granularity[key], values);
            if (refinedValues && refinedValues.length) {
                refinedFilters[key] = refinedValues;
            }
        }
    });

    return refinedFilters;
};

export const hasErrorInRefinementFilters = ({
    granularity,
    selectedConditions,
    selectedRefinementFilters,
}: {
    granularity: DataGranularity | null;
    selectedConditions: DataCondition[];
    selectedRefinementFilters: DataFilters;
}) => {
    // here are a list of filters that are not causing API error when null/undefined
    const API_NULLABLE_FILTERS = [
        YEAR_KEY,
        DAY_KEY,
        MONTH_COUNT_KEY,
        CONSTANT_VACCINATION_ID_KEY,
        EXPANDED_WOLBACHIA_ID_KEY,
    ];
    if (!granularity) {
        return false;
    }

    const primaryEntityFilterKeys = getConditionsDataTypes(selectedConditions).map(
        getDataTypePrimaryEntityKey
    );

    let errorInRefinements = false;
    Object.entries(granularity || {})
        // remove primary keys
        .filter(
            ([granularityKey, _]) =>
                ![...primaryEntityFilterKeys, ...API_NULLABLE_FILTERS].includes(granularityKey)
        )
        .forEach(([granularityKey, granularityValues]) => {
            if (isEmpty(granularityValues)) {
                return;
            }

            const filterValues = selectedRefinementFilters[granularityKey];
            if (filterValues == null) {
                errorInRefinements = true;
                return;
            }

            const allFilterValuesNotInGranularity = filterValues.every(
                (value) => !granularityValues.includes(value)
            );
            if (allFilterValuesNotInGranularity) {
                errorInRefinements = true;
            }
        });
    return errorInRefinements;
};
